import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Zap, Check, X, Trash2 } from "lucide-react";
import { useAgentConnections, useAgentConnectionRequests } from "@/api/queries/connections";
import { useAgents } from "@/api/queries/agents";
import {
  useAcceptConnectionRequest,
  useRejectConnectionRequest,
  useCancelConnectionRequest,
  useRevokeConnection,
} from "@/api/mutations/connections";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DateTimeCell } from "@/components/ResourceShell";
import type { Agent, AgentConnection } from "@/types";
import { RequestConnectionDialog } from "./RequestConnectionDialog";
import { DirectConnectionDialog } from "./DirectConnectionDialog";

interface Props {
  agent: Agent;
}

// PeerLink — resolved Peer-Id zum Peer-Agent via useAgents-Liste (orphan badge wenn weg).
function PeerLink({ peerId, agents }: { peerId: string; agents: Agent[] }) {
  const peer = agents.find((a) => a.id === peerId);
  if (peer) {
    return (
      <Link to={`/agents/${peerId}`} className="text-blue hover:underline">
        {peer.name} <span className="text-overlay0 text-xs font-mono">({peerId})</span>
      </Link>
    );
  }
  return <Badge variant="orphaned">orphaned: {peerId}</Badge>;
}

export function ConnectionsSection({ agent }: Props) {
  const conns = useAgentConnections(agent.id);
  const requests = useAgentConnectionRequests(agent.id, "pending");
  const agentsQuery = useAgents();
  const allAgents = agentsQuery.data ?? [];

  const acceptMut = useAcceptConnectionRequest();
  const rejectMut = useRejectConnectionRequest();
  const cancelMut = useCancelConnectionRequest();
  const revokeMut = useRevokeConnection();

  const [requestOpen, setRequestOpen] = useState(false);
  const [directOpen, setDirectOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<AgentConnection | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const inbox = useMemo(
    () => (requests.data ?? []).filter((r) => r.targetAgentId === agent.id),
    [requests.data, agent.id],
  );
  const outbox = useMemo(
    () => (requests.data ?? []).filter((r) => r.requesterAgentId === agent.id),
    [requests.data, agent.id],
  );
  const active = (conns.data ?? []).filter((c) => c.status === "active");

  function peerOf(conn: AgentConnection): string {
    return conn.agentAId === agent.id ? conn.agentBId : conn.agentAId;
  }

  function handleAccept(id: string) {
    acceptMut.mutate(id, {
      onSuccess: () => toast.success("Connection accepted"),
      onError: (err) => toast.error(`Accept fehlgeschlagen: ${(err as Error).message}`),
    });
  }

  function handleReject(id: string) {
    rejectMut.mutate(id, {
      onSuccess: () => toast.success("Request abgelehnt"),
      onError: (err) => toast.error(`Reject fehlgeschlagen: ${(err as Error).message}`),
    });
  }

  function handleCancel(req: { id: string; requesterAgentId: string; targetAgentId: string }) {
    cancelMut.mutate(
      { requestId: req.id, requesterAgentId: req.requesterAgentId, targetAgentId: req.targetAgentId },
      {
        onSuccess: () => toast.success("Request gecancelt"),
        onError: (err) => toast.error(`Cancel fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  function handleRevoke() {
    if (!revokeTarget) return;
    setRevokeError(null);
    revokeMut.mutate(
      { connectionId: revokeTarget.id, agentAId: revokeTarget.agentAId, agentBId: revokeTarget.agentBId },
      {
        onSuccess: () => {
          toast.success("Connection revoked");
          setRevokeTarget(null);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setRevokeError(msg);
          toast.error(`Revoke fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <>
      <Card data-testid="connections-section">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>A2A Connections</CardTitle>
            <div className="text-sm text-subtext0 mt-1">
              {active.length} aktiv · {inbox.length} eingehend · {outbox.length} ausgehend
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setRequestOpen(true)} data-testid="conn-action-request">
              <Plus className="h-4 w-4" />
              Request
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDirectOpen(true)}
              data-testid="conn-action-direct"
              title="Admin-only Direct-Insert (Service-Agents)"
            >
              <Zap className="h-4 w-4" />
              Direct
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 space-y-2">
          {/* Active Connections */}
          <div>
            <div className="px-6 py-2 text-xs text-subtext0 uppercase tracking-wide">Active</div>
            {active.length === 0 ? (
              <div className="p-6 text-sm text-subtext0 text-center" data-testid="conn-active-empty">
                Keine aktiven Connections
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Peer</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {active.map((c) => (
                    <TableRow key={c.id} data-testid={`conn-active-${c.id}`}>
                      <TableCell><PeerLink peerId={peerOf(c)} agents={allAgents} /></TableCell>
                      <TableCell className="text-xs"><DateTimeCell iso={c.createdAt} /></TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setRevokeTarget(c)}
                          aria-label="Revoke"
                          data-testid={`conn-revoke-${c.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Inbox */}
          {inbox.length > 0 && (
            <div className="border-t border-border">
              <div className="px-6 py-2 text-xs text-subtext0 uppercase tracking-wide">
                Inbox (eingehend pending)
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>From</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inbox.map((r) => (
                    <TableRow key={r.id} data-testid={`conn-inbox-${r.id}`}>
                      <TableCell>
                        <PeerLink peerId={r.requesterAgentId} agents={allAgents} />
                      </TableCell>
                      <TableCell className="text-xs text-subtext0">
                        {r.reason ?? <span className="text-overlay0">—</span>}
                      </TableCell>
                      <TableCell className="text-xs"><DateTimeCell iso={r.createdAt} /></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="default"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleAccept(r.id)}
                            aria-label="Accept"
                            data-testid={`conn-accept-${r.id}`}
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleReject(r.id)}
                            aria-label="Reject"
                            data-testid={`conn-reject-${r.id}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Outbox */}
          {outbox.length > 0 && (
            <div className="border-t border-border">
              <div className="px-6 py-2 text-xs text-subtext0 uppercase tracking-wide">
                Outbox (ausgehend pending)
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>To</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outbox.map((r) => (
                    <TableRow key={r.id} data-testid={`conn-outbox-${r.id}`}>
                      <TableCell>
                        <PeerLink peerId={r.targetAgentId} agents={allAgents} />
                      </TableCell>
                      <TableCell className="text-xs text-subtext0">
                        {r.reason ?? <span className="text-overlay0">—</span>}
                      </TableCell>
                      <TableCell className="text-xs"><DateTimeCell iso={r.createdAt} /></TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() =>
                            handleCancel({
                              id: r.id,
                              requesterAgentId: r.requesterAgentId,
                              targetAgentId: r.targetAgentId,
                            })
                          }
                          aria-label="Cancel"
                          data-testid={`conn-cancel-${r.id}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {requestOpen && (
        <RequestConnectionDialog open onOpenChange={setRequestOpen} sourceAgent={agent} />
      )}
      {directOpen && (
        <DirectConnectionDialog open onOpenChange={setDirectOpen} sourceAgent={agent} />
      )}
      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(o) => {
          if (!o) {
            setRevokeTarget(null);
            setRevokeError(null);
          }
        }}
        title="Connection revoken"
        description="Setzt status auf revoked. Peers können sich nicht mehr gegenseitig über mesh.send adressieren. Reversibel via neuer Request."
        confirmText="Revoke"
        confirmVariant="destructive"
        isPending={revokeMut.isPending}
        errorMessage={revokeError ?? undefined}
        onConfirm={handleRevoke}
      />
    </>
  );
}
